import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import "../../../styles/adminList.css";
import { saveAs } from "file-saver";

const BlogCategory = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const [monthFilter, setMonthFilter] = useState("");
  const monthQuery = monthFilter ? `&month=${monthFilter}` : "";
  let is_alert = false;

  const hasShownToast = useRef(false); // ✅ persist across renders

  useEffect(() => {
    if (location.state && !hasShownToast.current) {
      toast.success(location.state);
      hasShownToast.current = true;

      // ✅ clear the history state so it doesn't show again
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const [data, setData] = useState([]);
  const [totalpage, setTotalPage] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const handlePageClick = (e) => {
    setPage(e.selected + 1);
  };

  const BlogCategoryData = () => {
    axios
      .get(
        `${process.env.REACT_APP_URL}/admin/blog/category?page=${page}&search=${search}${monthQuery}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      )
      .then((res) => {
        // The response never actually had a top-level `.pages` field (it's
        // `.pagination.totalPages`), so this always set totalPage to
        // undefined and ReactPaginate silently showed no pages at all.
        setTotalPage(res?.data?.data?.pagination?.totalPages || 1);
        setData(res?.data?.data?.categories);
      })
      .catch((err) => {
        if (err.response?.data?.message === "Unauthorized!") {
          Navigate("/");
        }
      });
  };

  const HandleActiveDeactive = (id, status) => {
    let statusText = status ? "Deactive" : "Active";
    let Color = status ? "#DC3741" : "#1b55e2";

    Swal.fire({
      title: `Do you want to ${statusText} this Record?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: Color,
      confirmButtonText: statusText,
    }).then((result) => {
      if (result?.isConfirmed === true) {
        axios
          .post(
            `${process.env.REACT_APP_URL}/admin/blog/category/toggle-status/${id}`,
            { status: !status },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("Token")}`,
              },
            }
          )
          .then((res) => {
            if (res.status === 200) {
              toast.success(res?.data?.message);
              BlogCategoryData();
            }
          })
          .catch((err) => {
            const MSG = ["Token not found.", "Invalid token.", "Unauthorized."];
            const STATUS = [401, 403];
            if (
              STATUS.includes(err?.response?.status) ||
              MSG.includes(err?.response?.data?.msg)
            ) {
              localStorage.clear();
              Navigate("/admin/blog-category");
            } else {
              toast.error(err?.response?.data?.msg);
            }
          });
      }
    });
  };

  const HandleDelete = (id) => {
    Swal.fire({
      title: "Do you want to Delete this Record?",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#DC3741",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed === true) {
        axios
          .delete(`${process.env.REACT_APP_URL}/admin/blog/category/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
            },
          })
          .then((res) => {
            if (res.status === 200) {
              Swal.fire({
                title: "Deleted Successfully.",
                icon: "success",
                showConfirmButton: false,
                timer: 2000,
              });
              BlogCategoryData();
            }
          })
          .catch((err) => {
            const MSG = ["Token not found.", "Invalid token.", "Unauthorized."];
            const STATUS = [401, 403];
            if (
              STATUS.includes(err.response.status) ||
              MSG.includes(err.response.data.msg)
            ) {
              localStorage.clear();
              Navigate("/");
            } else {
              toast.error(err?.response?.data?.msg);
            }
          });
      }
    });
  };

  const exportToExcel = () => {
    if (data.length === 0) {
      alert("No data to export.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BlogCategory");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(fileData, "blog-category.xlsx");
  };

  useEffect(() => {
    BlogCategoryData();
  }, [page, search, monthFilter]);

  const initialsOf = (name = "") =>
    name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  const TINTS = [
    { bg: "var(--color-info-bg)", color: "var(--color-info-text)" },
    { bg: "var(--color-success-bg)", color: "var(--color-success-text)" },
    { bg: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
    { bg: "var(--color-violet-bg)", color: "var(--color-violet-text)" },
  ];
  const tint = (s = "") => TINTS[(String(s).length) % TINTS.length];
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleString("en-GB", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
        })
      : "—";

  const [dense, setDense] = useState(false);
  const [sortDir, setSortDir] = useState("");

  let displayData = [...(data || [])];
  if (sortDir) {
    displayData.sort((a, b) => {
      const av = (a?.category || "").toLowerCase();
      const bv = (b?.category || "").toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }

  const Toggle = ({ item }) => (
    <button type="button" className={`al-toggle ${item.is_active ? "is-on" : ""}`}
      title={item.is_active ? "Deactivate" : "Activate"}
      onClick={() => HandleActiveDeactive(item.blog_id, item.is_active)}>
      <span className="al-toggle__knob" />
    </button>
  );

  const RowActions = ({ item }) => (
    <div className="al-actions">
      <button type="button" className="al-act al-act--primary" title="Edit"
        onClick={() => Navigate("/admin/blog-category/edit", { state: { id: item?.blog_id } })}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>
      </button>
      <button type="button" className="al-act al-act--danger" title="Delete"
        onClick={() => HandleDelete(item?.blog_id)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
      </button>
    </div>
  );

  const GRID = { gridTemplateColumns: "1.6fr 1.2fr .8fr 96px" };

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Blog Categories</h1>
          <p className="al-sub">Topics used to organise blog posts.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={exportToExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export
          </button>
          <button type="button" className="al-btn al-btn--primary" onClick={() => Navigate("/admin/blog-category/add")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Blog Category
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-toolbar">
          <div className="al-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-tertiary)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input type="search" placeholder="Search blog categories…"
              onKeyDown={(e) => e.key === "Enter" && search !== "" && BlogCategoryData()}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="al-select">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">Select Month</option>
              <option value="All">All</option>
              <option value="1">Last 1 Month</option>
              <option value="3">Last 3 Months</option>
              <option value="6">Last 6 Months</option>
            </select>
          </div>
          <button type="button" className="al-tool" style={{ marginLeft: "auto" }} onClick={() => setDense((d) => !d)} title="Toggle density">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {dense ? "Comfortable" : "Density"}
          </button>
        </div>

        <div className="al-tablewrap">
          <div style={{ minWidth: 760, "--al-row-pad": dense ? "8px" : "13px" }}>
            <div className="al-thead" style={GRID}>
              <span className="al-th al-th--sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? "" : "asc"))}>
                Name
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: sortDir ? 1 : 0.45, transform: sortDir === "desc" ? "rotate(180deg)" : "none" }}><polyline points="18 15 12 9 6 15"/></svg>
              </span>
              <span className="al-th">Date &amp; Time</span>
              <span className="al-th">Is Active</span>
              <span className="al-th al-th--right">Action</span>
            </div>

            {displayData && displayData.length > 0 ? (
              displayData.map((item) => {
                const t = tint(item?.category);
                return (
                  <div className="al-row" style={GRID} key={item.blog_id}>
                    <div className="al-id">
                      <span className="al-avatar" style={{ background: t.bg, color: t.color }}>{initialsOf(item?.category)}</span>
                      <span className="al-name">{item?.category || "—"}</span>
                    </div>
                    <span className="al-mono al-muted">{fmt(item?.created_at)}</span>
                    <span><Toggle item={item} /></span>
                    <RowActions item={item} />
                  </div>
                );
              })
            ) : (
              <div className="al-empty">
                <div className="al-empty__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                </div>
                <p className="al-empty__title">No Blog Categories Found</p>
                <p className="al-empty__text">Try a different search or add a category.</p>
              </div>
            )}
          </div>
        </div>

        <div className="al-foot">
          <span className="al-foot__info">Showing <strong>{displayData?.length || 0}</strong> categories</span>
          {data && data.length !== 0 && (
            <ReactPaginate
              previousLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
              nextLabel={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>}
              breakLabel={"..."}
              pageCount={totalpage}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination justify-content-end m-0"}
              pageClassName={"page-item"} pageLinkClassName={"page-link"}
              previousClassName={"page-item"} previousLinkClassName={"page-link"}
              nextClassName={"page-item"} nextLinkClassName={"page-link"}
              breakClassName={"page-item"} breakLinkClassName={"page-link"}
              activeClassName={"active"} />
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogCategory;
